import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/brand/Logo";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Users, Building2, Cpu, AlertTriangle, DollarSign } from "lucide-react";

function Stat({ icon: Icon, label, value, tone = "amber" }) {
  const t = { amber: "text-amber-400", cyan: "text-cyan-400", red: "text-red-400", emerald: "text-emerald-400" }[tone];
  return (
    <div className="card-arch p-5">
      <div className="flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /><span className="mono-label">{label}</span></div>
      <p className={`mt-2 font-head text-3xl font-extrabold ${t}`}>{value}</p>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/admin/overview").then(({ data }) => setData(data)).catch((e) => toast.error(apiErr(e))); }, []);

  return (
    <div className="min-h-screen bg-obsidian">
      <header className="glass border-b border-white/10 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4"><Logo /><span className="mono-label text-red-400">ADMIN</span></div>
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate("/app/dashboard")} className="text-slate-400 hover:text-white"><ArrowLeft className="mr-2 h-4 w-4" /> App</Button>
          <Button variant="ghost" onClick={logout} className="text-slate-400 hover:text-red-400">Salir</Button>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 space-y-8">
        <h1 className="font-head text-2xl font-bold text-white">Panel de administración</h1>
        {!data ? <div className="mono-label text-slate-500">CARGANDO…</div> : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Stat icon={Users} label="Usuarios" value={data.users} tone="cyan" />
              <Stat icon={Building2} label="Workspaces" value={data.workspaces} tone="amber" />
              <Stat icon={Cpu} label="Créditos IA usados" value={data.credits_consumed} tone="amber" />
              <Stat icon={DollarSign} label="Coste IA (USD)" value={`$${data.ai_cost_usd}`} tone="emerald" />
              <Stat icon={AlertTriangle} label="Runs fallidos" value={data.failed_runs} tone="red" />
            </div>

            <div>
              <h2 className="font-head text-lg font-semibold text-white">AI Runs recientes</h2>
              <div className="mt-3 card-arch divide-y divide-white/5">
                {data.recent_runs.length === 0 && <p className="p-5 text-sm text-slate-500">Sin ejecuciones.</p>}
                {data.recent_runs.map((r) => (
                  <div key={r.id} className="flex items-center justify-between p-4 text-sm">
                    <div><span className="text-white">{r.run_type}</span><span className="ml-2 text-xs font-mono text-slate-500">{r.model}</span></div>
                    <div className="flex items-center gap-4 text-xs font-mono">
                      <span className="text-slate-400">{r.input_tokens}→{r.output_tokens} tok</span>
                      <span className="text-amber-400">−{r.credits_consumed} cr</span>
                      <span className={r.success ? "text-emerald-400" : "text-red-400"}>{r.success ? "OK" : "FAIL"}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="font-head text-lg font-semibold text-white">Ledger reciente</h2>
              <div className="mt-3 card-arch divide-y divide-white/5">
                {data.recent_ledger.map((e) => (
                  <div key={e.id} className="flex items-center justify-between p-4 text-sm">
                    <span className="text-white">{e.type}</span>
                    <span className={`font-mono ${e.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>{e.amount >= 0 ? "+" : ""}{e.amount} → {e.balance_after}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
